import axios from 'axios';
import { JWT_ACCESS_TOKEN_KEY, JWT_REFRESH_TOKEN_KEY } from './jwt-token-access';
import getAccessToken from './jwt-token-access';
import { openAuthModalEvent } from 'context/AuthModalEvent';

export const API_URL = process.env.REACT_APP_URL_STAGING;

const axiosApi = axios.create({
  baseURL: API_URL,
});

axiosApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const accessToken = localStorage.getItem(JWT_ACCESS_TOKEN_KEY);
      const refreshToken = localStorage.getItem(JWT_REFRESH_TOKEN_KEY);

      // Only attempt refresh if we have tokens (not a failed login attempt)
      if (accessToken && refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/api/access-token`, {
            refresh_token: refreshToken,
          });
          const newToken = response.data?.access_token;
          if (newToken) {
            localStorage.setItem(JWT_ACCESS_TOKEN_KEY, newToken);
            originalRequest.headers['Authorization'] = newToken;
            return axiosApi(originalRequest);
          }
        } catch {
          // Refresh failed — clear tokens and ask user to re-login
          localStorage.removeItem(JWT_ACCESS_TOKEN_KEY);
          localStorage.removeItem(JWT_REFRESH_TOKEN_KEY);
          openAuthModalEvent('expired');
        }
      }
    }

    return Promise.reject(error);
  },
);

function addBearerToken(config) {
  if (!localStorage.getItem(JWT_ACCESS_TOKEN_KEY)) {
    return config;
  }

  if ('headers' in config) {
    config['headers'] = {
      ...config['headers'],
      Authorization: getAccessToken(),
    };
  } else {
    config = {
      headers: {
        Authorization: getAccessToken(),
      },
    };
  }

  return config;
}

export const handleErrorRequest = (error) => {
  if (error.detail) {
    return error.detail;
  } else if (error.response) {
    if (error.response.data.errors) {
      let errors = error.response.data.errors.map((error) => {
        return error.detail;
      });

      if (errors.length > 0) {
        return errors.join('\n');
      }
    }
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    return error.response.data;
  } else if (error.request) {
    if (error.request.status === 404) {
      return error.request.statusText;
    }

    if (error.request.status === 0) {
      return 'Internal Server Error';
    }
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    return error.request.statusText;
  }

  // Something happened in setting up the request that triggered an Error
  return 'Internal Server Error';
};

export async function get(url, config = {}) {
  config = addBearerToken(config);
  return await axiosApi
    .get(url, config)
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}
export async function getCsv(url, fileName, config = {}) {
  config = addBearerToken(config);
  return await axiosApi
    .get(url, {
      ...config,
      responseType: 'blob', // This is important
    })
    .then((response) => {
      var blob = new Blob([response.data], {
        type: 'text/csv',
      });
      // fileDownload(blob, fileName);
      return { success: true };
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}

export async function post(url, data, config = {}) {
  config = addBearerToken(config);

  return axiosApi
    .post(url, { ...data }, { ...config })

    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error?.request?.status,
      };
      throw errorDetail;
    });
}

export async function put(url, data, config = {}) {
  config = addBearerToken(config);
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then((response) => response.data)
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}

export async function del(url, config = {}) {
  config = addBearerToken(config);
  return await axiosApi
    .delete(url, { ...config })
    .then((response) => response.data)
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}

export async function patch(url, data, config = {}) {
  config = addBearerToken(config);
  return axiosApi
    .patch(url, { ...data }, { ...config })
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}

export async function uploadFile(url, file, setProgress) {
  var formData = new FormData();
  formData.append('file', file);

  return await axiosApi
    .post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: getAccessToken(),
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(percentCompleted);
      },
    })
    .catch(function (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        throw error.response.data;
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.log(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log('Error', error.message);
      }
      console.log(error.config);
    });
}

export async function uploadFormData(url, data) {
  return await axiosApi
    .post(url, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: getAccessToken(),
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}

export async function uploadFormDataJson(url, data) {
  return await axiosApi
    .post(url, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAccessToken(),
        // 'Is-Live': `${CookieJs.get('environment') === 'production'}`,
        // 'Is-Live': `${ISLIVE_MERCHANT}`,
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}

export async function updateFormDataJson(url, data) {
  return await axiosApi
    .put(url, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAccessToken(),
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}

export async function getDownload(url) {
  return await axiosApi
    .get(url, {
      responseType: 'blob',
      headers: {
        Authorization: getAccessToken(),
      },
    })
    .then((response) => {
      return response;
    })
    .catch(function (error) {
      let errorDetail = {
        detail: handleErrorRequest(error),
        status_code: error.request.status,
      };
      throw errorDetail;
    });
}
